/**
 * Tab-scoped feature-flag overrides used by the dev tools panel.
 *
 *   - 'on'              -> force the flag to true / preserve its provider variant
 *   - 'off'             -> force the flag to false
 *   - 'variant:<value>' -> force the flag on with a specific string variant
 *   - undefined         -> use the provider's actual evaluation (no override)
 *
 * Persistence: backed by `sessionStorage` so overrides survive a hard
 * reload (essential because most flags are read at boot — refreshing is
 * how you get the override to take effect) but are dropped when the tab
 * is closed. This avoids the "forgotten override follows me forever"
 * footgun of `localStorage` while still being functional.
 *
 * Bridges that live outside React (PostHog override push, OpenFeature
 * override provider) consume the atom via the Jotai default store using
 * `getOverride` / `subscribeToOverrides`, not React hooks.
 */
import { getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { layerOverDefaults, readE2eFlagDefaults } from './e2e-flag-defaults';
import { getSnapshotOverride } from './snapshot-override';

export type FlagProvider = 'posthog' | 'flagsmith';

export type FlagOverrideValue = 'on' | 'off' | `variant:${string}` | `snapshot:${string}`;

const VARIANT_OVERRIDE_PREFIX = 'variant:';

export const createVariantOverride = (variant: string): FlagOverrideValue =>
  `${VARIANT_OVERRIDE_PREFIX}${variant}`;

export const getVariantOverride = (override: FlagOverrideValue): string | undefined =>
  override.startsWith(VARIANT_OVERRIDE_PREFIX)
    ? override.slice(VARIANT_OVERRIDE_PREFIX.length)
    : undefined;

/** Map key shape: `${provider}:${flagKey}`. */
export type FlagOverrideMap = Readonly<Record<string, FlagOverrideValue>>;

const EMPTY_OVERRIDES: FlagOverrideMap = Object.freeze({});

export const FLAG_OVERRIDES_STORAGE_KEY = 'heidi.devTools.flagOverrides';

const overrideKey = (provider: FlagProvider, flagKey: string) => `${provider}:${flagKey}`;

/** The pinned list a desktop e2e build boots with; empty everywhere else. */
const E2E_DEFAULTS: FlagOverrideMap = Object.freeze(readE2eFlagDefaults());

/**
 * SSR-safe sessionStorage adapter. Atom is read on the server during
 * Next.js page rendering, so we have to no-op when `window` is absent.
 *
 * Reads lay the stored map over `defaults` (see `layerOverDefaults`), so a
 * stored map holding one spec's pin cannot drop the rest of the e2e list.
 * Outside an e2e build `defaults` is empty and reads are unchanged.
 */
export const createFlagOverridesStorage = (defaults: FlagOverrideMap = E2E_DEFAULTS) => {
  // Written out rather than taken from `createJSONStorage`: the repo-wide
  // `jotai/utils` test stand-in replaces that helper with empty stubs, which
  // would leave this read untestable. Per-tab sessionStorage needs none of
  // its cross-tab `subscribe`.
  const session = (): Storage | null =>
    typeof window === 'undefined' ? null : window.sessionStorage;
  return {
    getItem: (key: string, initialValue: FlagOverrideMap): FlagOverrideMap => {
      let stored = initialValue;
      try {
        const raw = session()?.getItem(key);
        if (raw != null) stored = JSON.parse(raw) as FlagOverrideMap;
      } catch {
        // Unreadable or corrupt storage reads as "nothing stored".
      }
      return layerOverDefaults(stored, defaults);
    },
    setItem: (key: string, value: FlagOverrideMap): void => {
      session()?.setItem(key, JSON.stringify(value));
    },
    removeItem: (key: string): void => {
      session()?.removeItem(key);
    },
  };
};

const sessionStorageAdapter = createFlagOverridesStorage();

/**
 * Per-tab persisted overrides. Cleared automatically on tab close, but
 * preserved across reload so flag changes can actually be observed
 * after a refresh.
 *
 * Starts empty, except in a desktop e2e build, which starts from the pinned
 * list in `e2e-flag-defaults.ts`. A stored map is laid over that list key by
 * key (`createFlagOverridesStorage`), so an override a test sets through the
 * panel wins for its own flag without dropping the other pins.
 */
export const flagOverridesAtom = atomWithStorage<FlagOverrideMap>(
  FLAG_OVERRIDES_STORAGE_KEY,
  E2E_DEFAULTS,
  sessionStorageAdapter,
  // `getOnInit: true` ensures `getDefaultStore().get(...)` returns the
  // hydrated value the very first time it's called, not the default —
  // bridges call `applyOverrides()` synchronously after subscribing, so
  // they need the persisted state to be there immediately.
  { getOnInit: true }
);

flagOverridesAtom.debugLabel = 'flagOverridesAtom (dev-tools)';

/**
 * Read the current override (if any) for a given provider+flag.
 * Safe to call from non-React contexts (uses Jotai's default store).
 */
export const getOverride = (
  provider: FlagProvider,
  flagKey: string
): FlagOverrideValue | undefined => {
  const overrides = getDefaultStore().get(flagOverridesAtom);
  return overrides[overrideKey(provider, flagKey)];
};

/**
 * Convenience for callers that want the override pre-coerced to a boolean
 * (or `undefined` when no override is set, meaning "fall through").
 */
export const getBooleanOverride = (
  provider: FlagProvider,
  flagKey: string
): boolean | undefined => {
  const value = getOverride(provider, flagKey);
  if (value === undefined) return undefined;
  const snapshot = getSnapshotOverride(value);
  if (snapshot) return snapshot.enabled;
  if (value === 'off') return false;
  return true;
};

/**
 * Returns all overrides for a single provider as a plain Record
 * (omitting the `${provider}:` prefix). Useful for batched bridge writes
 * (e.g. PostHog `overrideFeatureFlags`).
 */
export const getOverridesForProvider = (
  provider: FlagProvider
): Record<string, FlagOverrideValue> => {
  const overrides = getDefaultStore().get(flagOverridesAtom);
  const prefix = `${provider}:`;
  const result: Record<string, FlagOverrideValue> = {};
  for (const key of Object.keys(overrides)) {
    if (key.startsWith(prefix)) {
      result[key.slice(prefix.length)] = overrides[key]!;
    }
  }
  return result;
};

/** Set or clear a single override. Pass `undefined` to clear. */
export const setOverride = (
  provider: FlagProvider,
  flagKey: string,
  value: FlagOverrideValue | undefined
): void => {
  const store = getDefaultStore();
  const current = store.get(flagOverridesAtom);
  const key = overrideKey(provider, flagKey);
  const existing = current[key];

  if (value === existing) return;

  const next: Record<string, FlagOverrideValue> = { ...current };
  if (value === undefined) {
    if (!(key in next)) return;
    delete next[key];
  } else {
    next[key] = value;
  }
  store.set(flagOverridesAtom, Object.keys(next).length === 0 ? EMPTY_OVERRIDES : next);
};

/**
 * Bulk-apply overrides in a single atom write. Used by the dev panel's
 * "Set all on / off" actions so subscribers fire once instead of once
 * per flag.
 */
export const setManyOverrides = (
  entries: ReadonlyArray<{
    provider: FlagProvider;
    flagKey: string;
    value: FlagOverrideValue | undefined;
  }>
): void => {
  if (entries.length === 0) return;
  const store = getDefaultStore();
  const current = store.get(flagOverridesAtom);
  const next: Record<string, FlagOverrideValue> = { ...current };
  let changed = false;
  for (const entry of entries) {
    const key = overrideKey(entry.provider, entry.flagKey);
    if (entry.value === undefined) {
      if (key in next) {
        delete next[key];
        changed = true;
      }
    } else if (next[key] !== entry.value) {
      next[key] = entry.value;
      changed = true;
    }
  }
  if (!changed) return;
  store.set(flagOverridesAtom, Object.keys(next).length === 0 ? EMPTY_OVERRIDES : next);
};

/**
 * Clear every override across all providers.
 *
 * In a desktop e2e build the baked-in pins survive this: the next read (a
 * reload) lays them back over the cleared map. Pin a flag explicitly to opt it
 * back on.
 */
export const clearAllOverrides = (): void => {
  const store = getDefaultStore();
  if (Object.keys(store.get(flagOverridesAtom)).length === 0) return;
  store.set(flagOverridesAtom, EMPTY_OVERRIDES);
};

/**
 * Subscribe to override changes from non-React code (PostHog bridge,
 * OpenFeature provider). Returns an unsubscribe fn.
 *
 * The callback fires synchronously after every `set` on the atom, so
 * keep work inside it cheap.
 */
export const subscribeToOverrides = (callback: () => void): (() => void) => {
  return getDefaultStore().sub(flagOverridesAtom, callback);
};

/**
 * Snapshot of overrides at page-load time. Captured once when this module
 * first evaluates on the client (`getOnInit: true` ensures the atom is
 * already hydrated from sessionStorage by then).
 *
 * Used by the dev panel to detect "you've changed something but haven't
 * refreshed yet" — most flags are read at boot, so a flip in the helper
 * has no UI effect until the next reload.
 */
const overridesAtPageLoad: FlagOverrideMap =
  typeof window === 'undefined'
    ? EMPTY_OVERRIDES
    : Object.freeze({ ...getDefaultStore().get(flagOverridesAtom) });

export const getOverridesAtPageLoad = (): FlagOverrideMap => overridesAtPageLoad;

/** Shallow equality on the override record. */
export const overridesEqual = (a: FlagOverrideMap, b: FlagOverrideMap): boolean => {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
};
